import { SearchIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { PatientMapping } from "../../../api/ehr";
import useMetriportToast from "../../shared/toast";
import { usePatientLookupQuery } from "../../shared/use-patient";

type PatientLookupProps = {
  ehrId: string;
  ehrName: string;
  cxId: string;
  setPatientMapping: (mapping: PatientMapping | undefined) => void;
};

export default function PatientLookup({
  ehrId,
  ehrName,
  cxId,
  setPatientMapping,
}: PatientLookupProps) {
  const toast = useMetriportToast();
  const [externalId, setExternalId] = useState("");
  const [lookupParams, setLookupParams] = useState<{ ehrId: string; externalId: string }>();

  const lookup = usePatientLookupQuery(lookupParams);

  useEffect(() => {
    setPatientMapping(lookup.data);
  }, [lookup.data, setPatientMapping]);

  function handleSearch() {
    const trimmed = externalId.trim();
    if (!trimmed) {
      toast.warning({ title: `Please enter a ${ehrName} Patient ID` });
      return;
    }
    setLookupParams({ ehrId, externalId: trimmed });
  }

  function handleInputChange(value: string) {
    setExternalId(value);
    if (lookupParams) setLookupParams(undefined);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !lookup.isFetching) {
      handleSearch();
    }
  }

  const result = lookup.data;
  const searched = lookup.isFetched;

  return (
    <Box w="full" maxW="4xl">
      <Text fontWeight="bold" mb={2} fontSize="lg">
        Patient Lookup
      </Text>
      <Text color="gray.600" mb={4} fontSize="sm">
        Look up a patient by their {ehrName} Patient ID.
      </Text>
      <FormControl mb={4}>
        <FormLabel>{ehrName} Patient ID</FormLabel>
        <Flex gap={2}>
          <Input
            value={externalId}
            onChange={e => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Enter ${ehrName} Patient ID`}
            bg="white"
          />
          <Button
            colorScheme="blue"
            onClick={handleSearch}
            isDisabled={lookup.isFetching}
            leftIcon={lookup.isFetching ? undefined : <SearchIcon />}
          >
            {lookup.isFetching ? <Spinner size="sm" /> : "Search"}
          </Button>
        </Flex>
      </FormControl>
      {searched && (
        <Box mt={4} p={4} bg="gray.50" borderRadius="md">
          {result ? (
            <VStack align="start" spacing={2}>
              {cxId != null && result.cxId !== cxId && (
                <Alert status="warning" borderRadius="md" w="full">
                  <AlertTitle>Different customer</AlertTitle>
                  <AlertDescription>
                    This patient belongs to customer <strong>{result.cxId}</strong>, not the current
                    customer (<strong>{cxId}</strong>).
                  </AlertDescription>
                </Alert>
              )}
              <MappingField label="Customer ID" value={result.cxId} />
              <MappingField label="Patient ID" value={result.patientId} />
              <MappingField label="External ID" value={result.externalId} />
              <MappingField label="Source" value={result.source} />
              {result.secondaryMappings?.practiceId && (
                <MappingField label="Practice ID" value={result.secondaryMappings.practiceId} />
              )}
              <MappingField label="Created At" value={result.createdAt} />
              <MappingField label="Updated At" value={result.updatedAt} />
            </VStack>
          ) : (
            <Text color="gray.500">No mapping found for this patient ID</Text>
          )}
        </Box>
      )}
    </Box>
  );
}

function MappingField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text fontWeight="semibold" fontSize="sm" color="gray.600">
        {label}:
      </Text>
      <Text fontFamily="mono" fontSize="sm">
        {value}
      </Text>
    </Box>
  );
}
